import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft, Send, UserPlus, XCircle, Clock, MessageSquare,
  User, Phone, Mail, FileText, RefreshCw, ArrowRightLeft, AlertCircle, Paperclip, Download, Image
} from 'lucide-react';
import apiService from '../services/api';
import signalRService from '../services/signalr';

const BACKEND_BASE = window.CHATBOT_CONFIG?.API_URL
  ? window.CHATBOT_CONFIG.API_URL.replace(/\/api$/, '')
  : `http://${window.location.hostname}:5500`;

const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_BASE}${url}`;
};

const PatientPanel = ({ currentUser, onBack }) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('list'); // list, chat
  const [activeFilter, setActiveFilter] = useState('waiting'); // waiting, mine, all, closed
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [queueSummary, setQueueSummary] = useState({ waitingCount: 0, activeCount: 0, myActiveCount: 0, todayClosedCount: 0 });
  const [users, setUsers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showLogsPanel, setShowLogsPanel] = useState(false);
  const [sessionLogs, setSessionLogs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      let params = '';
      if (activeFilter === 'waiting') params = '?status=0';
      else if (activeFilter === 'mine') params = '?mine=true&status=1';
      else if (activeFilter === 'closed') params = '?status=3';

      const data = await apiService.request(`/patient/sessions${params}`);
      setSessions(data || []);
    } catch (err) {
      console.error('Sessions load error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  const loadQueueSummary = useCallback(async () => {
    try {
      const data = await apiService.request('/patient/queue/summary');
      setQueueSummary(data);
    } catch (err) {
      console.error('Queue summary error:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await apiService.request('/admin/users');
      setUsers(data || []);
    } catch (err) {
      console.error('Users load error:', err);
    }
  }, []);

  useEffect(() => {
    loadSessions();
    loadQueueSummary();
    loadUsers();
  }, [loadSessions, loadQueueSummary, loadUsers]);

  // SignalR listeners
  useEffect(() => {
    const handleQueueUpdate = () => {
      loadSessions();
      loadQueueSummary();
    };

    const handlePatientMessage = (msg) => {
      if (selectedSession && msg.sessionId === selectedSession.id) {
        // Skip if sent by me (already added from API response) or if already exists
        if (msg.senderUserId === currentUser?.id && msg.senderType === 1) return;
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
      loadQueueSummary();
    };

    const handleSessionAssigned = (session) => {
      // Transfer notification - play sound and show alert
      if (session.assignedToUserId === currentUser?.id) {
        try {
          const audio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZUPOZqAAAAAAD/+1DEAAAGAAGn9AAAIgAANP8AAARMQWME//Lpf5c//+X5d/l//////0uk//wy/y7/y7/l0v/+WQwYDBhghh/l3+Xf5dL/y7/hgwY2NjBhYWFhYWFhYf/y6X/4Y/DH8u/y7////+XS4YMbG5ubm5u7u7u7u7u7/+7u7u7u7u7u7v/u7u7u7u7u7u7u7u7u7u7v/u7u7u7u7u7//+1DEKAPHA15/8AAAIAAANP8AAABu7u7u7u7u7u7u7u7u7u7//7u7u7u7u7u7u7u7u7u7u7/+7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u4=');
          audio.volume = 0.5;
          audio.play().catch(() => {});
        } catch {}

        loadSessions();
        loadQueueSummary();
      }
    };

    signalRService.connection?.on('PatientSessionAssigned', handleSessionAssigned);
    signalRService.connection?.on('PatientQueueUpdated', handleQueueUpdate);
    signalRService.connection?.on('PatientMessage', handlePatientMessage);
    signalRService.connection?.on('PatientSessionCreated', handleQueueUpdate);

    return () => {
      signalRService.connection?.off('PatientSessionAssigned', handleSessionAssigned);
      signalRService.connection?.off('PatientQueueUpdated', handleQueueUpdate);
      signalRService.connection?.off('PatientMessage', handlePatientMessage);
      signalRService.connection?.off('PatientSessionCreated', handleQueueUpdate);
    };
  }, [selectedSession, loadSessions, loadQueueSummary]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openSession = async (session) => {
    setSelectedSession(session);
    setActiveView('chat');
    setShowLogsPanel(false);
    try {
      const msgs = await apiService.request(`/patient/sessions/${session.id}/messages`);
      setMessages(msgs || []);
      // Mark as read and update unread count in list
      await apiService.request(`/patient/sessions/${session.id}/read`, { method: 'POST' });
      setSessions(prev => prev.map(s => s.id === session.id ? { ...s, unreadCount: 0 } : s));
      loadQueueSummary();
      // Join SignalR group
      signalRService.connection?.invoke('JoinPatientSession', session.id);
    } catch (err) {
      console.error('Load messages error:', err);
    }
  };

  const sendMessage = async (attachmentUrl, attachmentName) => {
    if ((!message.trim() && !attachmentUrl) || !selectedSession) return;
    const content = message.trim() || (attachmentName || 'Dosya');
    setMessage('');
    try {
      const msg = await apiService.request(`/patient/sessions/${selectedSession.id}/message`, {
        method: 'POST',
        body: JSON.stringify({ content, attachmentUrl, attachmentName })
      });
      setMessages(prev => [...prev, msg]);
    } catch (err) {
      console.error('Send error:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSession) return;
    setUploading(true);
    try {
      const uploaded = await apiService.uploadFile(file);
      await sendMessage(uploaded.fileUrl, uploaded.fileName);
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const pickSession = async (sessionId) => {
    try {
      const updated = await apiService.request(`/patient/sessions/${sessionId}/pick`, { method: 'POST' });
      setSelectedSession(updated);
      loadSessions();
      loadQueueSummary();
      openSession(updated);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAssign = async () => {
    if (!assignUserId || !selectedSession) return;
    try {
      const updated = await apiService.request(`/patient/sessions/${selectedSession.id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ toUserId: parseInt(assignUserId), reason: assignReason })
      });
      setSelectedSession(updated);
      setShowAssignModal(false);
      setAssignUserId('');
      setAssignReason('');
      loadSessions();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleClose = async () => {
    if (!selectedSession) return;
    try {
      const updated = await apiService.request(`/patient/sessions/${selectedSession.id}/close`, {
        method: 'POST',
        body: JSON.stringify({ closeReason })
      });
      setSelectedSession(updated);
      setShowCloseModal(false);
      setCloseReason('');
      loadSessions();
      loadQueueSummary();
    } catch (err) {
      alert(err.message);
    }
  };

  const goBack = () => {
    if (activeView === 'chat') {
      if (selectedSession) {
        signalRService.connection?.invoke('LeavePatientSession', selectedSession.id);
      }
      setActiveView('list');
      setSelectedSession(null);
      setMessages([]);
    } else {
      onBack();
    }
  };

  const deleteSession = async (sessionId, e) => {
    e?.stopPropagation();
    if (!window.confirm(t('patient.deleteConfirm'))) return;
    try {
      await apiService.request(`/patient/sessions/${sessionId}`, { method: 'DELETE' });
      loadSessions();
      loadQueueSummary();
      if (selectedSession?.id === sessionId) goBack();
    } catch (err) {
      alert(err.message || t('patient.deleteFailed'));
    }
  };

  const loadLogs = async (sessionId) => {
    try {
      const logs = await apiService.request(`/patient/sessions/${sessionId}/logs`);
      setSessionLogs(logs || []);
      setShowLogsPanel(true);
    } catch (err) {
      console.error('Logs error:', err);
    }
  };

  const logActionLabels = {
    0: t('patient.logCreated'), 1: t('patient.logAssigned'), 2: t('patient.logTransferred'),
    3: t('patient.logMessage'), 4: t('patient.logClosed'), 5: t('patient.logReopened'), 6: t('patient.logNoteAdded')
  };

  const statusColors = { 0: '#f59e0b', 1: '#22c55e', 2: '#8b5cf6', 3: '#9ca3af' };
  const statusLabels = { 0: t('patient.waiting'), 1: t('patient.active'), 2: t('patient.transferred'), 3: t('patient.closedStatus') };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }) + ' ' +
      d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // =====================================================
  // CHAT VIEW
  // =====================================================
  if (activeView === 'chat' && selectedSession) {
    const isClosed = selectedSession.status === 3;
    const isAssignedToMe = selectedSession.assignedToUserId === currentUser?.id;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f9fafb' }}>
        {/* Chat Header */}
        <div style={{
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={goBack} style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}>
              <ChevronLeft size={18} />
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>{selectedSession.patientName}</div>
              <div style={{ fontSize: '11px', opacity: 0.9 }}>
                {selectedSession.subject || statusLabels[selectedSession.status]}
              </div>
            </div>
            <button onClick={() => setShowInfoPanel(!showInfoPanel)}
              style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}>
              <User size={16} />
            </button>
            {!isClosed && isAssignedToMe && (
              <>
                <button onClick={() => setShowAssignModal(true)}
                  style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
                  title={t('patient.assign')}>
                  <ArrowRightLeft size={16} />
                </button>
                <button onClick={() => setShowCloseModal(true)}
                  style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
                  title={t('patient.close')}>
                  <XCircle size={16} />
                </button>
              </>
            )}
            {!isClosed && !isAssignedToMe && selectedSession.status === 0 && (
              <button onClick={() => pickSession(selectedSession.id)}
                style={{ background: 'rgba(255,255,255,0.3)', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: '600' }}>
                {t('patient.pickSession')}
              </button>
            )}
            <button onClick={() => loadLogs(selectedSession.id)}
              style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
              title={t('patient.logs')}>
              <FileText size={16} />
            </button>
            {(currentUser?.isAdmin || currentUser?.canDeletePatientMessages) && (
              <button onClick={(e) => deleteSession(selectedSession.id, e)}
                style={{ background: 'rgba(255,0,0,0.3)', borderRadius: '8px', padding: '6px' }}
                title="Sil">
                <XCircle size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Info Panel (slide) */}
        {showInfoPanel && (
          <div style={{ padding: '12px', background: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '13px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {selectedSession.patientPhone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                  <Phone size={12} /> {selectedSession.patientPhone}
                </div>
              )}
              {selectedSession.patientEmail && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                  <Mail size={12} /> {selectedSession.patientEmail}
                </div>
              )}
              {selectedSession.assignedToUserName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                  <User size={12} /> {t('patient.assignedTo')}: {selectedSession.assignedToUserName}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                <Clock size={12} /> {formatTime(selectedSession.createdAt)}
              </div>
            </div>
            {selectedSession.note && (
              <div style={{ marginTop: '8px', padding: '8px', background: '#fffbeb', borderRadius: '6px', fontSize: '12px', color: '#92400e' }}>
                <FileText size={12} style={{ display: 'inline', marginRight: '4px' }} />
                {selectedSession.note}
              </div>
            )}
          </div>
        )}

        {/* Logs Panel */}
        {showLogsPanel && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'white', zIndex: 50, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>{t('patient.logs')}</h4>
              <button onClick={() => setShowLogsPanel(false)} style={{ padding: '4px' }}>
                <XCircle size={18} color="#6b7280" />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
              {sessionLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>{t('patient.logsEmpty')}</div>
              ) : sessionLogs.map(log => (
                <div key={log.id} style={{ display: 'flex', gap: '10px', padding: '10px', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f59e0b', marginTop: '6px', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>
                      {logActionLabels[log.action] || log.actionText}
                      {log.userName && <span style={{ color: '#6b7280', fontWeight: '400' }}> - {log.userName}</span>}
                    </div>
                    {log.details && <div style={{ color: '#6b7280', marginTop: '2px', fontSize: '12px' }}>{log.details}</div>}
                    <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>{formatTime(log.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          {messages.map(msg => {
            const isSystem = msg.senderType === 2;
            const isStaff = msg.senderType === 1;

            if (isSystem) {
              return (
                <div key={msg.id} style={{ textAlign: 'center', margin: '8px 0' }}>
                  <span style={{ fontSize: '11px', color: '#9ca3af', background: '#f3f4f6', padding: '4px 12px', borderRadius: '12px' }}>
                    {msg.content}
                  </span>
                </div>
              );
            }

            return (
              <div key={msg.id} style={{
                display: 'flex',
                justifyContent: isStaff ? 'flex-end' : 'flex-start',
                marginBottom: '8px',
                gap: '6px',
                alignItems: 'flex-end'
              }}>
                {/* Patient avatar */}
                {!isStaff && (
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '10px', fontWeight: '600', flexShrink: 0
                  }}>
                    {(msg.senderName || selectedSession.patientName || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                )}
                <div style={{
                  maxWidth: '70%',
                  padding: '10px 14px',
                  borderRadius: isStaff ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: isStaff ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'white',
                  color: isStaff ? 'white' : '#1f2937',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  {!isStaff && (
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#f59e0b', marginBottom: '2px' }}>
                      {msg.senderName || selectedSession.patientName}
                    </div>
                  )}
                  {isStaff && msg.senderName && (
                    <div style={{ fontSize: '11px', fontWeight: '600', opacity: 0.85, marginBottom: '2px' }}>
                      {msg.senderName}
                    </div>
                  )}
                  {msg.attachmentUrl && (
                    msg.attachmentName?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <a href={getFileUrl(msg.attachmentUrl)} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '4px' }}>
                        <img src={getFileUrl(msg.attachmentUrl)} alt={msg.attachmentName} style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
                      </a>
                    ) : (
                      <a href={getFileUrl(msg.attachmentUrl)} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px', background: 'rgba(0,0,0,0.1)',
                          borderRadius: '8px', marginBottom: '4px', color: 'inherit', textDecoration: 'none', fontSize: '12px' }}>
                        <Download size={14} /> {msg.attachmentName || 'Dosya'}
                      </a>
                    )
                  )}
                  {msg.content && !msg.attachmentUrl && <div style={{ fontSize: '14px', lineHeight: '1.4' }}>{msg.content}</div>}
                  {msg.content && msg.attachmentUrl && msg.content !== msg.attachmentName && <div style={{ fontSize: '14px', lineHeight: '1.4' }}>{msg.content}</div>}
                  <div style={{ fontSize: '10px', opacity: 0.7, textAlign: 'right', marginTop: '4px' }}>
                    {formatTime(msg.sentAt)}
                  </div>
                </div>
                {/* Staff avatar */}
                {isStaff && (
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '10px', fontWeight: '600', flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {msg.senderAvatar ? (
                      <img src={getFileUrl(msg.senderAvatar)} alt={msg.senderName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      (msg.senderName || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {!isClosed && isAssignedToMe ? (
          <div style={{ padding: '12px', background: 'white', borderTop: '1px solid #e5e7eb' }}>
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f3f4f6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                title={t('patient.send')}>
                {uploading ? <RefreshCw size={16} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} /> : <Paperclip size={16} color="#6b7280" />}
              </button>
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={t('patient.typeMessage')}
                style={{
                  flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb',
                  borderRadius: '20px', fontSize: '14px', outline: 'none'
                }}
              />
              <button onClick={() => sendMessage()} disabled={!message.trim()} style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: message.trim() ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white'
              }}>
                <Send size={18} />
              </button>
            </div>
          </div>
        ) : isClosed ? (
          <div style={{ padding: '16px', background: '#f3f4f6', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
            <AlertCircle size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
            {t('patient.sessionClosed')}
          </div>
        ) : (
          <div style={{ padding: '12px', background: '#fffbeb', textAlign: 'center', fontSize: '13px', color: '#92400e' }}>
            {t('patient.pickSession')}
          </div>
        )}

        {/* Assign Modal */}
        {showAssignModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
            <div style={{ background: 'white', borderRadius: '12px', width: '100%', maxWidth: '350px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600' }}>{t('patient.assignTo')}</h3>
              <select value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '12px', fontSize: '14px' }}>
                <option value="">{t('patient.assignTo')}...</option>
                {users.filter(u => u.isActive && u.id !== currentUser?.id).map(u => (
                  <option key={u.id} value={u.id}>{u.fullName}</option>
                ))}
              </select>
              <input type="text" value={assignReason} onChange={(e) => setAssignReason(e.target.value)}
                placeholder={t('patient.assignReason')}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px', fontSize: '14px', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setShowAssignModal(false)}
                  style={{ flex: 1, padding: '10px', background: '#f3f4f6', borderRadius: '8px', fontSize: '14px' }}>
                  {t('patient.cancel')}
                </button>
                <button onClick={handleAssign} disabled={!assignUserId}
                  style={{ flex: 1, padding: '10px', background: assignUserId ? '#f59e0b' : '#e5e7eb', color: 'white', borderRadius: '8px', fontSize: '14px', fontWeight: '500' }}>
                  {t('patient.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close Modal */}
        {showCloseModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
            <div style={{ background: 'white', borderRadius: '12px', width: '100%', maxWidth: '350px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600' }}>{t('patient.close')}</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>{t('patient.closeConfirm')}</p>
              <input type="text" value={closeReason} onChange={(e) => setCloseReason(e.target.value)}
                placeholder={t('patient.closeReason')}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px', fontSize: '14px', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setShowCloseModal(false)}
                  style={{ flex: 1, padding: '10px', background: '#f3f4f6', borderRadius: '8px', fontSize: '14px' }}>
                  {t('patient.cancel')}
                </button>
                <button onClick={handleClose}
                  style={{ flex: 1, padding: '10px', background: '#ef4444', color: 'white', borderRadius: '8px', fontSize: '14px', fontWeight: '500' }}>
                  {t('patient.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =====================================================
  // LIST VIEW
  // =====================================================
  const filters = [
    { id: 'waiting', label: t('patient.queue'), count: queueSummary.waitingCount, color: '#f59e0b' },
    { id: 'mine', label: t('patient.myChats'), count: queueSummary.myActiveCount, color: '#22c55e' },
    { id: 'all', label: t('patient.all'), count: queueSummary.activeCount },
    { id: 'closed', label: t('patient.closed'), count: queueSummary.todayClosedCount, color: '#9ca3af' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}>
            <ChevronLeft size={18} />
          </button>
          <MessageSquare size={20} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>{t('patient.title')}</div>
          </div>
          <button onClick={() => { loadSessions(); loadQueueSummary(); }}
            style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        {filters.map(f => (
          <button key={f.id} onClick={() => setActiveFilter(f.id)}
            style={{
              flex: 1, padding: '10px 8px', fontSize: '12px', fontWeight: activeFilter === f.id ? '600' : '400',
              color: activeFilter === f.id ? '#d97706' : '#6b7280',
              borderBottom: activeFilter === f.id ? '2px solid #d97706' : '2px solid transparent',
              background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
            }}>
            {f.label}
            {f.count > 0 && (
              <span style={{
                minWidth: '18px', height: '18px', borderRadius: '9px', fontSize: '10px', fontWeight: '700',
                background: f.color || '#e5e7eb', color: 'white',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px'
              }}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Session List */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <RefreshCw size={24} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
            <MessageSquare size={40} style={{ marginBottom: '8px', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>{t('patient.noSessions')}</p>
          </div>
        ) : (
          sessions.map(session => (
            <button key={session.id} onClick={() => openSession(session)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
                padding: '14px 16px', background: 'white', borderBottom: '1px solid #f3f4f6',
                textAlign: 'left', cursor: 'pointer', transition: 'background 0.1s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fefce8'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>

              {/* Avatar */}
              <div style={{
                width: '42px', height: '42px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${statusColors[session.status]} 0%, ${statusColors[session.status]}cc 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: '600', fontSize: '14px', flexShrink: 0
              }}>
                {session.patientName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>{session.patientName}</span>
                  <span style={{
                    padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '600',
                    background: statusColors[session.status] + '20', color: statusColors[session.status]
                  }}>
                    {statusLabels[session.status]}
                  </span>
                </div>
                {session.subject && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {session.subject}
                  </div>
                )}
                {session.lastMessage && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {session.lastMessage}
                  </div>
                )}
              </div>

              {/* Right side */}
              <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                  {formatTime(session.lastMessageAt || session.createdAt)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {session.unreadCount > 0 && (
                    <div style={{
                      minWidth: '20px', height: '20px', borderRadius: '10px',
                      background: '#f59e0b', color: 'white', fontSize: '11px', fontWeight: '700',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px'
                    }}>
                      {session.unreadCount}
                    </div>
                  )}
                  {(currentUser?.isAdmin || currentUser?.canDeletePatientMessages) && (
                    <button onClick={(e) => deleteSession(session.id, e)}
                      style={{ padding: '4px', background: '#fef2f2', borderRadius: '4px', border: 'none', cursor: 'pointer', display: 'flex' }}
                      title={t('patient.delete')}>
                      <XCircle size={14} color="#dc2626" />
                    </button>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default PatientPanel;
